import { useRef } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { LogOut } from "lucide-react";

interface ExitConfirmationDialogProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title?: string;
  description?: string;
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function ExitConfirmationDialog({
  open,
  onConfirm,
  onCancel,
  title = "Leave Dashboard?",
  description = "You're about to leave the dashboard. Any unsaved changes may be lost.",
}: ExitConfirmationDialogProps) {
  // Track if user clicked confirm to prevent onCancel being called on dialog close
  const confirmedRef = useRef(false);

  const handleConfirm = () => {
    confirmedRef.current = true;
    onConfirm();
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && !confirmedRef.current) {
      // Only call onCancel if dialog is closing and user didn't click confirm
      onCancel();
    }
    // Reset the flag when dialog opens
    if (isOpen) {
      confirmedRef.current = false;
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="mx-auto w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mb-2">
            <LogOut className="h-6 w-6 text-destructive" />
          </div>
          <AlertDialogTitle className="text-center">{title}</AlertDialogTitle>
          <AlertDialogDescription className="text-center">
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="sm:justify-center gap-2">
          <AlertDialogCancel onClick={onCancel}>
            Stay on Dashboard
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Leave Dashboard
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
